import { ComponentIds, Entity } from '@inter-ikea-core/core';
import { CoreApi, ProductType } from '@inter-ikea-storageone/range';

export const productTypesNotInScene = (coreApi: CoreApi, productTypes: ProductType[]) => {
  return coreApi.entities.getByProductTypes(productTypes).length === 0;
};

/**
 * Finds the leftmost connected frame to input frame. Or returns the input frame
 * if that is the leftmost one.
 * @param f Frame entity
 * @returns Frame entity
 */
export const getLeftMostFrame = (coreApi: CoreApi, f: Entity): Entity => {
  const af = coreApi.entities.getAlignedFrames(f, 'align-left', 'align-right');
  if (af.left) {
    return getLeftMostFrame(coreApi, af.left);
  }
  return f;
};

/**
 * Finds the rightmost connected frame to input frame. Or returns the input frame
 * if that is the rightmost one.
 * @param f Frame entity
 * @returns Frame entity
 */
export const getRightMostFrame = (coreApi: CoreApi, f: Entity): Entity => {
  const af = coreApi.entities.getAlignedFrames(f, 'align-left', 'align-right');
  if (af.left) {
    return getLeftMostFrame(coreApi, af.left);
  }
  return f;
};

/**
 * Finds and return input frame and all it's connected frames to the right of it.
 * @param f Frame entity
 * @returns List of frame entities
 */
const traverseRight = (coreApi: CoreApi, f: Entity): Entity[] => {
  const af = coreApi.entities.getAlignedFrames(f, 'align-left', 'align-right');
  if (af.right) {
    return [f, ...traverseRight(coreApi, af.right)];
  }
  return [f];
};

export const getAllFrameGroups = (coreApi: CoreApi, allFrames: Entity[]): Entity[][] => {
  const groups = [] as Entity[][];

  // Go through all frames and sort them into groups until
  // there are no more frames in the list.
  while (allFrames.length > 0) {
    const currentFrame = allFrames[0];

    const insertFrameIntoGroups = (f: Entity) => {
      // Find the leftmost frame connected to current frame. Or the frame itself.
      const leftMostFrame = getLeftMostFrame(coreApi, f);
      // Traverse right through the leftmost frames right connections and store all frames in a list.
      // This will be the whole group. From leftmost to rightmost frame.
      const frameGroup = traverseRight(coreApi, leftMostFrame);

      // Remove all frames in current group from list of all frames. We don't want
      // to process frames we already put into a group.
      frameGroup.forEach(f => {
        allFrames = allFrames.filter(af => af.id !== f.id);
      });

      // Finally add current group to list of groups.
      groups.push(frameGroup);
    };

    insertFrameIntoGroups(currentFrame);
  }

  return groups;
};

/**
 * Checks if the frames in the scene is in exactly one group and not wider
 * than 4.5 meters
 * @param coreApi CoreApi
 * @returns true or false
 */
export const sceneHasSingleFrameGroup = (coreApi: CoreApi): boolean => {
  const groupedFrames = getAllFrameGroups(
    coreApi,
    coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID)
  );

  const outerMeasurements = coreApi.entities.getOuterMeasurements();
  const frameGroupWidth =
    groupedFrames.length === 1 && outerMeasurements ? outerMeasurements.width : 0;

  const selectedEntity = coreApi.entities.findSelectedEntity();

  const frames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const selectedFrames = selectedEntity
    ? frames.filter(frame => frame.id === selectedEntity.id)
    : null;

  return (
    groupedFrames.length === 1 &&
    groupedFrames[0].length > 1 &&
    frameGroupWidth < 4501 &&
    selectedFrames?.length !== 0
  );
};
